/**
 * The knowledge wiki's markdown dialect, client side: just enough to *render* a
 * file. Resolution of `[[link]]` targets happens on the backend; the file
 * response carries `links` (target text as written → readable target), so the
 * client never re-implements it. Kept in step with
 * backend/src/services/knowledge_graph/dialect.py.
 */

/** A YAML frontmatter block at the very top of a file. */
const FRONTMATTER = /^\uFEFF?---\r?\n[\s\S]*?\r?\n---[ \t]*(?:\r?\n|$)/;

/** `![[body]]{props}`: embed marker, link body, optional typed-edge props. */
const WIKILINK = /(!?)\[\[([^[\]\n]+?)\]\](?:\{[^{}\n]*\})?/g;

export function stripFrontmatter(content: string): string {
  return content.replace(FRONTMATTER, "");
}

export interface WikilinkParts {
  target: string;
  anchor: string | null;
  display: string | null;
}

/** `"Target#anchor|display"` → parts, split like the backend (`|` first, then `#`). */
export function parseWikilinkBody(body: string): WikilinkParts {
  let rest = body;
  let display: string | null = null;
  const bar = rest.indexOf("|");
  if (bar !== -1) {
    display = rest.slice(bar + 1).trim() || null;
    rest = rest.slice(0, bar);
  }
  let anchor: string | null = null;
  const hash = rest.indexOf("#");
  if (hash !== -1) {
    anchor = rest.slice(hash + 1).trim() || null;
    rest = rest.slice(0, hash);
  }
  return { target: rest.trim(), anchor, display };
}

/** In-app URL of a wiki file. */
export function wikiFileHref(spaceUuid: string, path: string): string {
  return `/spaces/${spaceUuid}/knowledge?file=${encodeURIComponent(path)}`;
}

export function isWikiFileHref(href: string | undefined): href is string {
  return typeof href === "string" && /^\/spaces\/[^/]+\/knowledge\?/.test(href);
}

// Minimal mdast shapes: only what the transform touches.
interface MdText {
  type: "text";
  value: string;
}
interface MdLink {
  type: "link";
  url: string;
  children: MdText[];
}
interface MdParent {
  type: string;
  children?: MdNode[];
}
type MdNode = MdText | MdLink | MdParent;

/** Don't create links inside these (no nested links; code is never a link). */
const SKIP = new Set(["link", "linkReference", "inlineCode", "code", "html"]);

function splitText(value: string, resolve: (target: string) => string | null): MdNode[] | null {
  WIKILINK.lastIndex = 0;
  if (!WIKILINK.test(value)) return null;
  WIKILINK.lastIndex = 0;
  const out: MdNode[] = [];
  let last = 0;
  for (const match of value.matchAll(WIKILINK)) {
    const start = match.index ?? 0;
    if (start > last) out.push({ type: "text", value: value.slice(last, start) });
    const { target, display } = parseWikilinkBody(match[2]);
    const label = display ?? target;
    const url = target ? resolve(target) : null;
    // Unresolved, unreadable and attachment links all render as plain text alike.
    out.push(url ? { type: "link", url, children: [{ type: "text", value: label }] } : { type: "text", value: label });
    last = start + match[0].length;
  }
  if (last < value.length) out.push({ type: "text", value: value.slice(last) });
  return out;
}

function transform(node: MdNode, resolve: (target: string) => string | null): void {
  if (!("children" in node) || !node.children || SKIP.has(node.type)) return;
  const next: MdNode[] = [];
  for (const child of node.children) {
    const replaced = child.type === "text" ? splitText((child as MdText).value, resolve) : null;
    if (replaced) {
      next.push(...replaced);
    } else {
      transform(child, resolve);
      next.push(child);
    }
  }
  node.children = next;
}

/**
 * remark plugin: `[[Target|label]]` → a link where `resolve(target)` returns a
 * URL, else the label as plain text. `{props}` after a link are edge metadata
 * and are dropped from the rendered text.
 */
export function remarkWikilinks(options: { resolve: (target: string) => string | null }) {
  return (tree: MdNode) => transform(tree, options.resolve);
}
